import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { Timeline } from "primereact/timeline";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { TbInbox, TbPlaneDeparture, TbPlane, TbBuildingWarehouse, TbTruckDelivery, TbCircleCheck } from "react-icons/tb";
import { DEV } from "../../services/constants";
import styles from "./TrackShipment.module.scss";

const STAFF_ROLES = ["admin", "handler", "clerk", "driver"];

// Every package follows the same fixed 6-step sequence - no other status
// is ever recorded against it (see recordTrackingEvent on the backend).
// There are two distinct facility touchpoints - Florida (before the flight)
// and Jamaica (after it, the Richmond Hub) - kept as separate stages so the
// order actually matches what happens chronologically.
const STAGE_STEPS = [
    { key: "received_fl", label: "Received in FL" },
    { key: "ready_for_flight", label: "Ready for Flight" },
    { key: "in_transit", label: "In Transit" },
    { key: "at_facility", label: "Package Arrived" },
    { key: "out_for_delivery", label: "Out for Delivery" },
    { key: "delivered", label: "Delivered" },
];

// The pickup track (Richmond Hub self-collection) uses different labels for
// the last two steps than the home-delivery track - a package can switch
// between tracks at any point, so this isn't a fixed property of a package,
// just an alternate label set for the same stage keys (see isRichmond).
const RICHMOND_STAGE_STEPS = [
    { key: "received_fl", label: "Received in FL" },
    { key: "ready_for_flight", label: "Ready for Flight" },
    { key: "in_transit", label: "In Transit" },
    { key: "at_facility", label: "Package Arrived" },
    { key: "out_for_delivery", label: "At Richmond Hub" },
    { key: "delivered", label: "Picked Up" },
];

const STAGE_ICON_COMPONENTS = {
    received_fl: TbInbox,
    ready_for_flight: TbPlaneDeparture,
    in_transit: TbPlane,
    at_facility: TbBuildingWarehouse,
    out_for_delivery: TbTruckDelivery,
    delivered: TbCircleCheck,
};

const formatTimestamp = (value) => {
    if (!value) return "";
    return new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const TrackShipment = () => {
    const { code } = useParams();
    const history = useHistory();
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [data, setData] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [searchValue, setSearchValue] = useState("");

    const role = localStorage.getItem("role");
    const isStaff = STAFF_ROLES.includes(role);
    const searchPlaceholder = isStaff ? "Search by tracking code, tracking number, or Waybill" : "Search by Waybill or tracking number";

    useEffect(() => {
        let mounted = true;

        // No code yet (e.g. navigated here from the menu link rather than a
        // specific tracking URL) - nothing to look up, so skip straight to
        // the landing/search prompt instead of firing a doomed request.
        if (!code) {
            setLoading(false);
            setNotFound(false);
            setData(null);
            return undefined;
        }

        const fetchTracking = async () => {
            setLoading(true);
            setNotFound(false);
            try {
                // TEMP_MOCK_FOR_SCREENSHOT_REVIEW
                // const mock = {
                //     trackingCode: "ZZ4F2A9C1D8E01",
                //     trackingNumber: "SPXMIA013672607010002796",
                //     currentStatus: "Package Arrived",
                //     currentStage: "at_facility",
                //     isRichmond: false,
                //     events: [
                //         { status: "Package Created", stage: "label_created", timestamp: "2026-07-01T00:50:00Z" },
                //         { status: "Received in FL", stage: "received_fl", location: "Fort Lauderdale, FL", timestamp: "2026-07-02T09:00:00Z" },
                //         { status: "Ready for Flight", stage: "in_transit", location: "Fort Lauderdale, FL", timestamp: "2026-07-03T09:00:00Z" },
                //         { status: "Package Arrived", stage: "at_facility", location: "Richmond Hub, Jamaica", timestamp: "2026-07-05T12:35:00Z" },
                //     ],
                // };
                // await new Promise((resolve) => setTimeout(resolve, 150));
                // if (mounted) setData(mock);
                // return;
                // eslint-disable-next-line no-unreachable
                const response = await axios.get(`${DEV}/track/${code}`, {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });
                if (mounted && response.data?.success) {
                    setData(response.data.data);
                } else if (mounted) {
                    setNotFound(true);
                }
            } catch (error) {
                if (mounted) setNotFound(true);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchTracking();
        return () => {
            mounted = false;
        };
    }, [code]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchValue.trim()) {
            history.push(`/track/${encodeURIComponent(searchValue.trim())}`);
        }
    };

    const searchBar = (
        <form className={styles.searchBar} onSubmit={handleSearch}>
            <i className="pi pi-search" />
            <InputText value={searchValue} onChange={(e) => setSearchValue(e.target.value)} placeholder={searchPlaceholder} className={styles.searchInput} />
            <button type="submit" className={styles.searchButton}>
                Track
            </button>
        </form>
    );

    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    {searchBar}
                    <Skeleton width="100%" height="70px" className={styles.skeletonBlock} />
                    <Skeleton width="100%" height="90px" className={styles.skeletonBlock} />
                    <Skeleton width="100%" height="280px" />
                </div>
            </div>
        );
    }

    if (!code) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    {searchBar}
                    <div className={styles.notFound}>
                        <i className="pi pi-search" />
                        <h2>Track your shipment</h2>
                        <p>Enter a tracking code, tracking number, or Waybill above to see its status.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (notFound || !data) {
        return (
            <div className={styles.page}>
                <div className={styles.container}>
                    {searchBar}
                    <div className={styles.notFound}>
                        <i className="pi pi-search" />
                        <h2>We couldn't find that shipment</h2>
                        <p>Double-check the tracking code and try again, or contact support if the problem continues.</p>
                    </div>
                </div>
            </div>
        );
    }

    const activeStageSteps = data.isRichmond ? RICHMOND_STAGE_STEPS : STAGE_STEPS;
    const stageIndex = Math.max(
        activeStageSteps.findIndex((step) => step.key === data.currentStage),
        0,
    );
    const orderedEvents = data.events ? [...data.events].reverse() : [];
    const mostRecentEvent = orderedEvents[0];
    const MostRecentIcon = mostRecentEvent ? STAGE_ICON_COMPONENTS[mostRecentEvent.stage] || TbCircleCheck : null;

    const displayId = data.Waybill || data.trackingNumber || data.trackingCode;

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                {searchBar}
                <header className={styles.header}>
                    <div>
                        <span className={styles.eyebrow}>Tracking</span>
                        <h1>{displayId}</h1>
                        {data.trackingNumber && data.trackingNumber !== displayId && <p className={styles.trackingNumber}>Carrier tracking: {data.trackingNumber}</p>}
                    </div>
                    <span className={`${styles.statusPill} ${styles[data.currentStage] || ""}`}>{data.currentStatus}</span>
                </header>

                <section className={`${styles.card} ${styles.stepperCard}`}>
                    <div className={styles.desktopSteps}>
                        <div className={styles.desktopStepsTrack}>
                            {activeStageSteps.map((step, index) => {
                                const Icon = STAGE_ICON_COMPONENTS[step.key] || TbCircleCheck;
                                const isActive = index <= stageIndex;
                                return (
                                    <React.Fragment key={step.key}>
                                        <div className={styles.desktopStep}>
                                            <span className={`${styles.desktopStepIcon} ${isActive ? `${styles.desktopStepIconActive} ${styles[step.key] || ""}` : ""}`}>
                                                <Icon />
                                            </span>
                                            <span className={`${styles.desktopStepLabel} ${isActive ? styles.desktopStepLabelActive : ""}`}>{step.label}</span>
                                        </div>
                                        {index < activeStageSteps.length - 1 && <span className={`${styles.desktopStepLine} ${index < stageIndex ? styles.desktopStepLineActive : ""}`} />}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {mostRecentEvent && (
                    <section className={`${styles.card} ${styles.recentEventCard}`}>
                        <h2>Most Recent Event</h2>
                        <div className={styles.recentEventStatus}>
                            <span className={`${styles.recentEventIcon} ${styles[mostRecentEvent.stage] || ""}`}>
                                <MostRecentIcon />
                            </span>
                            <span className={styles.recentEventStatusText}>{mostRecentEvent.status}</span>
                        </div>
                        <div className={styles.recentEventMeta}>
                            <div>
                                <span className={styles.metaLabel}>Date &amp; Time</span>
                                <span>{formatTimestamp(mostRecentEvent.timestamp)}</span>
                            </div>
                            {mostRecentEvent.location && (
                                <div>
                                    <span className={styles.metaLabel}>Location</span>
                                    <span>{mostRecentEvent.location}</span>
                                </div>
                            )}
                        </div>
                        {mostRecentEvent.note && <p className={styles.recentEventNote}>{mostRecentEvent.note}</p>}
                        {mostRecentEvent.images?.length > 0 && (
                            <div className={styles.podImages}>
                                {mostRecentEvent.images.map((src, index) => (
                                    <button key={index} type="button" className={styles.podThumbButton} onClick={() => setPreviewImage(src)}>
                                        <img src={src} alt="Proof of delivery" className={styles.podThumb} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                <section className={`${styles.card} ${styles.timelineCard}`}>
                    <h2>Shipment Activity</h2>
                    {orderedEvents.length ? (
                        <Timeline
                            value={orderedEvents}
                            align="left"
                            className={styles.timeline}
                            marker={(event) => {
                                const Icon = STAGE_ICON_COMPONENTS[event.stage] || TbCircleCheck;
                                return (
                                    <span className={`${styles.marker} ${styles[event.stage] || ""}`}>
                                        <Icon />
                                    </span>
                                );
                            }}
                            content={(event) => (
                                <div className={styles.eventContent}>
                                    <div className={styles.eventTop}>
                                        <span className={styles.eventStatus}>{event.status}</span>
                                        <span className={styles.eventTime}>{formatTimestamp(event.timestamp)}</span>
                                    </div>
                                    {event.location && (
                                        <div className={styles.eventLocation}>
                                            <i className="pi pi-map-marker" /> {event.location}
                                        </div>
                                    )}
                                    {event.note && <div className={styles.eventNote}>{event.note}</div>}
                                </div>
                            )}
                        />
                    ) : (
                        <p className={styles.emptyState}>No activity recorded yet.</p>
                    )}
                </section>

                <Dialog visible={!!previewImage} onHide={() => setPreviewImage(null)} className={styles.previewDialog} breakpoints={{ "960px": "90vw" }}>
                    {previewImage && <img src={previewImage} alt="Proof of delivery" className={styles.previewImage} />}
                </Dialog>
            </div>
        </div>
    );
};

export default TrackShipment;
